import { BRAND } from "constants/brand";
import { useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClose } from "@fortawesome/free-solid-svg-icons";

import { Form } from "react-bootstrap";

import { CSSTransition } from "react-transition-group";
import { Ring } from "@uiball/loaders";
import { updateVendorPassword } from "services/vendors";

/**
 * "تغيير كلمة المرور" — changes one vendor's password and nothing else.
 *
 * WHY THIS IS ITS OWN MODAL. The two password inputs used to sit in the vendor
 * edit form, sharing its submit button with delivery time, cover image and the
 * rest. That form posted them on EVERY save, and the API mass-assigned the body,
 * so an operator editing a delivery time also sent `password: ""` — which
 * overwrote the stored bcrypt hash with an empty string. `bcrypt.compare(x, "")`
 * is always false, so the vendor could never log in again, with no error at edit
 * time and nothing afterwards to distinguish it from a forgotten password. 50 of
 * 306 vendor accounts were already in that state when this was found.
 *
 * The API-side guard stops the bleeding; this modal removes the shape that
 * caused it. A password now only leaves the browser when someone deliberately
 * set out to change one.
 *
 * Posts to a dedicated endpoint (PATCH vendor/:id/password) rather than
 * edit-vendor, which no longer accepts credentials at all.
 */
const ChangeVendorPassword = (props) => {
    const form = useRef();
    const nodeRef = useRef();
    const [loading, setLoading] = useState(false);

    const submitHandler = async (e) => {
        e.preventDefault();

        const data = new FormData(form.current);
        const password = String(data.get("password") || "");
        const confirmPassword = String(data.get("confirm_password") || "");

        // Checked here as well as on the server so the operator finds out before
        // a round trip; the server is still the real gate.
        if (password !== confirmPassword) {
            props.showMessage("error", "هناك خطأ", "كلمتا المرور غير متطابقتين");
            return;
        }

        if (password.length < 6) {
            props.showMessage("error", "هناك خطأ", "كلمة المرور قصيرة جدًا");
            return;
        }

        setLoading(true);
        try {
            await updateVendorPassword(props.vendorId, {
                password,
                confirm_password: confirmPassword,
            });
            props.showMessage(
                "success",
                "تم",
                "تم تغيير كلمة المرور وتسجيل خروج المطعم من كل الأجهزة",
            );
            form.current.reset();
            props.close();
        } catch (err) {
            props.showMessage(
                "error",
                "هناك خطأ",
                err?.response?.data?.message || "تعذر تغيير كلمة المرور",
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <div className={`model ${props.show ? "model-show" : ""}`} onClick={props.close}>
                <CSSTransition
                    mountOnEnter
                    unmountOnExit
                    nodeRef={nodeRef}
                    in={props.show}
                    timeout={400}
                    classNames="show"
                >
                    <div className="addItem" ref={nodeRef} onClick={(e) => e.stopPropagation()}>
                        {loading ? (
                            <div className="loading-spinner center">
                                <Ring size={40} lineWeight={5} speed={2} color={BRAND} />
                            </div>
                        ) : null}
                        <div className="head center">
                            <h5>تغيير كلمة المرور</h5>
                            <FontAwesomeIcon icon={faClose} onClick={props.close} />
                        </div>
                        <Form className="form" ref={form} onSubmit={submitHandler}>
                            {props.vendorName ? (
                                <p style={{ marginBottom: "16px", color: "#6c757d" }}>
                                    {props.vendorName}
                                </p>
                            ) : null}

                            <div className="form-warning">
                                بعد الحفظ يتم تسجيل خروج هذا المطعم من كل الأجهزة، ويتوقف وصول
                                إشعارات الطلبات إليها. يجب تسجيل الدخول على جهاز المطعم من جديد
                                بكلمة المرور الجديدة.
                            </div>

                            <Form.Group className="mb-3">
                                <Form.Label>
                                    كلمه المرور الجديدة<span style={{ color: "red" }}>*</span>
                                </Form.Label>
                                <Form.Control
                                    name="password"
                                    type="password"
                                    autoComplete="new-password"
                                    minLength={6}
                                    required
                                />
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label>
                                    تأكيد كلمه المرور<span style={{ color: "red" }}>*</span>
                                </Form.Label>
                                <Form.Control
                                    name="confirm_password"
                                    type="password"
                                    autoComplete="new-password"
                                    minLength={6}
                                    required
                                />
                            </Form.Group>

                            <div className="buttons">
                                <button className="cancel" onClick={props.close} type="button">
                                    إلغاء
                                </button>
                                <button className="button" type="submit" disabled={loading}>
                                    حفظ
                                </button>
                            </div>
                        </Form>
                    </div>
                </CSSTransition>
            </div>
        </>
    );
};

export default ChangeVendorPassword;
